import {
  Badge,
  Divider,
  Link,
  Stack,
  ViewHeader,
  ViewItemField,
  ViewItemFieldWithChildren,
} from '@fukuri-admins/ui/common';
import { SectionTitle } from './SectionTitle';
import styles from './styles.module.scss';

/**
 * 所在地3行の型定義
 */
type AddressLines = {
  /** 郵便番号（〒XXX-XXXX 形式、未設定の場合は '-'） */
  postalCode: string;
  /** 都道府県 + 市区町村（未設定の場合は '-'） */
  prefectureCity: string;
  /** 番地以下（未設定の場合は '-'） */
  streetAddress: string;
};

/**
 * フィーチャーフラグの型定義
 */
type ClientFeatureFlag = {
  /** フィーチャーフラグのキー */
  featureKey: string;
  /** フィーチャーフラグの値 */
  isEnabled: boolean;
  /** フィーチャーフラグの表示用ラベル */
  label: string;
};

/** ViewItemField のラベルが受け取るヘルプオブジェクトの型（content はこのファイルで組み立てる） */
type HeaderHelpContent = NonNullable<React.ComponentProps<typeof ViewItemField>['helpContent']>;

/**
 * カスタムフィールドの型定義
 */
type CustomField = {
  /** カスタムフィールド定義ID（React key用） */
  id: number;
  /** ラベル */
  label: string;
  /** 表示値 */
  value: string;
};

type Props = {
  /**
   * 会社名（タイトルおよび会社名フィールドに表示）
   */
  clientName: string;
  /**
   * 編集画面への URL
   */
  editLink: string;
  // ───────── 会社情報 ─────────
  /** 会社名フリガナ */
  companyNameKana: string;
  /** 代表者氏名 */
  representativeName: string;
  /** 法人番号 */
  corporateNumber: string;
  /** 外部企業ID */
  externalClientId: string;
  /** グループ名（複数の場合「, 」区切り） */
  groupName: string;
  /** 所在地（3行） */
  address: AddressLines;
  /** 申込人数（フォーマット済み文字列。改称前は利用予定者数） */
  employeeCount: string;
  /** 取引店舗名（null 時は '-'。改称前はお取引店舗） */
  partnerBranchName: string;
  /** 利用人数（フォーマット済み文字列。改称前は利用者数） */
  userCount: string;
  /** 課金対象人数（フォーマット済み文字列。改称前は課金対象者数） */
  nonInvitedUserCount: string;
  /** カスタムフィールド一覧 */
  customFields: CustomField[];
  /**
   * 人数項目の項目名を改称後（申込人数 / 利用人数 / 課金対象人数）で表示するか
   *
   * false のときは従来の項目名（利用予定者数 / 利用者数 / 課金対象者数）を表示する
   */
  showRevisedHeadcountLabels: boolean;
  /**
   * 取引店舗を所在地の直後に「取引店舗」の項目名で表示するか
   *
   * false のときは従来どおり利用予定者数の直後に「お取引店舗」の項目名で表示する
   */
  showRevisedPartnerBranchField: boolean;
  // ───────── 登録案内カード ─────────
  /** 登録案内カードセクションを表示するか */
  showActivationCardSection: boolean;
  /** 送付先住所（3行。未登録の場合は3行とも '-'） */
  shippingAddress: AddressLines;
  // ───────── プラン情報 ─────────
  /** 契約プラン名（contract_plan が null の場合は '-'） */
  contractPlanName: string;
  /** 契約プラン詳細画面への遷移パス（contract_plan が null の場合は空文字列） */
  contractPlanLink: string;
  /** 利用開始日（現在契約中のプランの開始日を YYYY/MM/DD 形式にしたもの。契約が無い場合は '-'） */
  contractStartedOn: string;
  /** クライアント機能フラグ一覧（未指定時は空配列として扱う想定） */
  clientFeatureFlags?: ClientFeatureFlag[];
  // ───────── 担当者情報 ─────────
  /** 担当者氏名 */
  contactPersonName: string;
  /** 担当者氏名（フリガナ） */
  contactPersonNameKana: string;
  /** メールアドレス */
  contactPersonEmail: string;
  /** 電話番号 */
  contactPersonPhone: string;
  /** お電話がつながりやすい時間帯（平日） */
  contactPreferredTime: string;
  // ───────── 請求先情報 ─────────
  /** 請求担当者氏名 */
  billingContactName: string;
  /** 請求担当者氏名フリガナ */
  billingContactNameKana: string;
  /** 請求書送付先メールアドレス */
  billingEmail: string;
  // ───────── 作成・更新日時 ─────────
  /** 作成日時（フォーマット済み文字列） */
  createdAt: string;
  /** 更新日時（フォーマット済み文字列） */
  updatedAt: string;
  /** 利用人数（改称前は利用者数）のヘルプアイコンのツールチップ表示ハンドラー */
  showTooltip: HeaderHelpContent['showHelp'];
  /** ツールチップを非表示にするハンドラー */
  hideTooltip: HeaderHelpContent['hideHelp'];
  /** ツールチップの id */
  tooltipId: HeaderHelpContent['helpId'];
};

/**
 * クライアント詳細 Presentation
 *
 * 会社情報 / 登録案内カード（showActivationCardSection 時のみ） / プラン情報（機能フラグ統合） /
 * 担当者情報 / 請求先情報 / 作成・更新日時 のセクションで表示する。
 *
 * @param clientName - 会社名
 * @param editLink - 編集画面への URL
 * @param companyNameKana - 会社名フリガナ
 * @param representativeName - 代表者氏名
 * @param corporateNumber - 法人番号
 * @param externalClientId - 外部企業ID
 * @param groupName - グループ名
 * @param address - 所在地3行
 * @param employeeCount - 申込人数
 * @param partnerBranchName - 取引店舗名
 * @param userCount - 利用人数
 * @param nonInvitedUserCount - 課金対象人数
 * @param customFields - カスタムフィールド一覧
 * @param showRevisedHeadcountLabels - 人数項目の項目名を改称後で表示するか
 * @param showRevisedPartnerBranchField - 取引店舗を所在地の直後に改称後の項目名で表示するか
 * @param showActivationCardSection - 登録案内カードセクションを表示するか
 * @param shippingAddress - 送付先住所3行
 * @param contractPlanName - 契約プラン名
 * @param contractPlanLink - 契約プラン詳細画面への遷移パス
 * @param contractStartedOn - 利用開始日
 * @param clientFeatureFlags - クライアント機能フラグ一覧
 * @param contactPersonName - 担当者氏名
 * @param contactPersonNameKana - 担当者氏名（フリガナ）
 * @param contactPersonEmail - メールアドレス
 * @param contactPersonPhone - 電話番号
 * @param contactPreferredTime - お電話がつながりやすい時間帯（平日）
 * @param billingContactName - 請求担当者氏名
 * @param billingContactNameKana - 請求担当者氏名フリガナ
 * @param billingEmail - 請求書送付先メールアドレス
 * @param createdAt - 作成日時
 * @param updatedAt - 更新日時
 * @param showTooltip - 利用人数のヘルプアイコンのツールチップ表示ハンドラー
 * @param hideTooltip - ツールチップを非表示にするハンドラー
 * @param tooltipId - ツールチップの id
 */
export const ClientDetailPresentation: React.FC<Props> = ({
  clientName,
  editLink,
  companyNameKana,
  representativeName,
  corporateNumber,
  externalClientId,
  groupName,
  address,
  employeeCount,
  partnerBranchName,
  userCount,
  nonInvitedUserCount,
  customFields,
  showRevisedHeadcountLabels,
  showRevisedPartnerBranchField,
  showActivationCardSection,
  shippingAddress,
  contractPlanName,
  contractPlanLink,
  contractStartedOn,
  clientFeatureFlags,
  contactPersonName,
  contactPersonNameKana,
  contactPersonEmail,
  contactPersonPhone,
  contactPreferredTime,
  billingContactName,
  billingContactNameKana,
  billingEmail,
  createdAt,
  updatedAt,
  showTooltip,
  hideTooltip,
  tooltipId,
}) => (
  <Stack gap={24} className={styles.content}>
    <ViewHeader title={clientName} editLink={editLink} />

    <Stack gap={32}>
      {/* 会社情報 */}
      <Stack gap={24}>
        <SectionTitle title='会社情報' />
        <Stack gap={24}>
          <ViewItemField label='法人番号' direction='row' value={corporateNumber} />
          <ViewItemField label='会社名' direction='row' value={clientName} />
          <ViewItemField label='会社名（フリガナ）' direction='row' value={companyNameKana} />
          <ViewItemField label='代表者氏名' direction='row' value={representativeName} />
          <ViewItemFieldWithChildren label='所在地' direction='row'>
            <Stack gap={8}>
              <span>{address.postalCode}</span>
              <span>{address.prefectureCity}</span>
              <span>{address.streetAddress}</span>
            </Stack>
          </ViewItemFieldWithChildren>
          {showRevisedPartnerBranchField && (
            <ViewItemField label='取引店舗' direction='row' value={partnerBranchName} />
          )}
          <ViewItemField
            label={showRevisedHeadcountLabels ? '申込人数' : '利用予定者数'}
            direction='row'
            value={employeeCount}
          />
          {!showRevisedPartnerBranchField && (
            <ViewItemField label='お取引店舗' direction='row' value={partnerBranchName} />
          )}
          <ViewItemField
            label={showRevisedHeadcountLabels ? '利用人数' : '利用者数'}
            direction='row'
            value={userCount}
            helpContent={{
              content: '家族アカウントも含みます。',
              showHelp: showTooltip,
              hideHelp: hideTooltip,
              helpId: tooltipId,
            }}
          />
          <ViewItemField
            label={showRevisedHeadcountLabels ? '課金対象人数' : '課金対象者数'}
            direction='row'
            value={nonInvitedUserCount}
          />
          <ViewItemField label='外部企業ID' direction='row' value={externalClientId} />
          <ViewItemField label='グループ' direction='row' value={groupName} />
          {customFields.map(field => (
            <ViewItemField key={field.id} label={field.label} direction='row' value={field.value} />
          ))}
        </Stack>
      </Stack>

      <Divider />

      {/* 登録案内カード（送付を希望する場合のみ） */}
      {showActivationCardSection && (
        <>
          <Stack gap={24}>
            <SectionTitle title='登録案内カード' />
            <Stack gap={24}>
              <ViewItemFieldWithChildren label='送付先' direction='row'>
                <Stack gap={8}>
                  <span>{shippingAddress.postalCode}</span>
                  <span>{shippingAddress.prefectureCity}</span>
                  <span>{shippingAddress.streetAddress}</span>
                </Stack>
              </ViewItemFieldWithChildren>
            </Stack>
          </Stack>

          <Divider />
        </>
      )}

      {/* プラン情報（機能フラグを統合） */}
      <Stack gap={24}>
        <SectionTitle title='プラン情報' />
        <Stack gap={24}>
          {contractPlanLink ? (
            <ViewItemFieldWithChildren label='契約プラン' direction='row'>
              <Link to={contractPlanLink} isExternal target='_blank'>
                {contractPlanName}
              </Link>
            </ViewItemFieldWithChildren>
          ) : (
            <ViewItemField label='契約プラン' direction='row' value='-' />
          )}
          <ViewItemField label='利用開始日' direction='row' value={contractStartedOn} />
          {clientFeatureFlags?.map(flag => (
            <ViewItemFieldWithChildren key={flag.featureKey} label={flag.label} direction='row'>
              <Badge
                size='md'
                variant='subtle'
                status={flag.isEnabled ? 'success' : 'neutral'}
                label={flag.isEnabled ? '有効' : '無効'}
                showClose={false}
              />
            </ViewItemFieldWithChildren>
          ))}
        </Stack>
      </Stack>

      <Divider />

      {/* 担当者情報 */}
      <Stack gap={24}>
        <SectionTitle title='担当者情報' />
        <Stack gap={24}>
          <ViewItemField label='担当者氏名' direction='row' value={contactPersonName} />
          <ViewItemField
            label='担当者氏名（フリガナ）'
            direction='row'
            value={contactPersonNameKana}
          />
          <ViewItemField label='メールアドレス' direction='row' value={contactPersonEmail} />
          <ViewItemField label='電話番号' direction='row' value={contactPersonPhone} />
          <ViewItemField
            label='お電話がつながりやすい時間帯（平日）'
            direction='row'
            value={contactPreferredTime}
          />
        </Stack>
      </Stack>

      <Divider />

      {/* 請求先情報 */}
      <Stack gap={24}>
        <SectionTitle title='請求先情報' />
        <Stack gap={24}>
          <ViewItemField label='請求担当者氏名' direction='row' value={billingContactName} />
          <ViewItemField
            label='請求担当者氏名（フリガナ）'
            direction='row'
            value={billingContactNameKana}
          />
          <ViewItemField label='請求書送付先メールアドレス' direction='row' value={billingEmail} />
        </Stack>
      </Stack>

      <Divider />

      {/* 作成・更新日時（セクションタイトルなし） */}
      <Stack gap={24}>
        <ViewItemField label='作成日時' direction='row' value={createdAt} />
        <ViewItemField label='更新日時' direction='row' value={updatedAt} />
      </Stack>
    </Stack>
  </Stack>
);
